import { isPosManaged } from '../../utils/pos-provider.util';
import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { CommonModule } from '@angular/common';
import { ConfigService } from '../../services/config.service';
import { RestaurantService } from '../../services/restaurant.service';
import { FNFResponse } from '../../models/response';
import { SettingService } from '../../services/setting.service';
import { SquareResyncService } from '../../services/square-resync.service';
import {
  canManageSocialMedia,
  getLoggedInUserRole,
  isPlatformSupportUser,
  PLATFORM_SUPPORT_MENU_TITLES,
} from '../../utils/user-role.util';
import {
  isLimitedAccessStatus,
  isMenuTitleAllowedForStatus,
} from '../../utils/restaurant-status.util';
import {
  menuTitlesLockedForPlan,
  minimumPlanForTitle,
} from '../../utils/subscription-plan-menu.util';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../utils/feature-flag.util';
import { ReservationService } from '../../services/reservation.service';
import { REQUEST_MODES, ReservationMode } from '../../models/reservation';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnDestroy {
  @Input() activeTab: string = 'Dashboard';
  @Output() notifyParent: EventEmitter<string> = new EventEmitter<string>();
  private authService = inject(AuthService);
  configService = inject(ConfigService);
  restaurantService = inject(RestaurantService);
  settingService = inject(SettingService);
  private squareResyncService = inject(SquareResyncService);
  private reservationService = inject(ReservationService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  isSidebarExpanded: boolean = true;
  currentRestaurant: any;
  currentPath: string = '';
  imageErrors: Set<string | number> = new Set();
  restaurantPosProvider: string = 'fnf';
  currentPlanName: string | null = null;
  featureFlags: FeatureFlag[] | null = null;
  reservationMode: ReservationMode | null = null;
  /** Booking requests waiting for an answer, on the Reservations entry (FNF-308). */
  pendingRequestCount = 0;
  private destroyed = false;
  user: any;

  // Flat navigation: every entry is a leaf link; group '' renders without a
  // heading (Home). Role/feature filters remove items by title and an empty
  // group just disappears.
  //
  // This is the master list — configureSideMenus() always filters down from
  // a fresh copy of it (never mutates in place), so a plan upgrade/downgrade
  // mid-session can restore items a previous, more-restrictive call removed.
  private readonly baseMenuItems: any[] = [
    { title: 'Home', icon: 'home', route: 'dashboard', group: '', selected: false },

    { title: 'Orders', icon: 'receipt_long', route: 'order', group: 'Operations', selected: false },
    {
      title: 'Tables',
      icon: 'table_restaurant',
      route: 'table',
      group: 'Operations',
      selected: false,
    },
    {
      title: 'QR codes',
      icon: 'qr_code_2',
      route: 'qr-codes',
      group: 'Operations',
      selected: false,
    },
    {
      title: 'Reservations',
      icon: 'event_available',
      route: 'reservation',
      group: 'Operations',
      selected: false,
    },

    { title: 'Menu', icon: 'restaurant', route: 'menu', group: 'Menu', selected: false },
    {
      title: 'Types & Categories',
      icon: 'category',
      route: 'type-and-categories',
      group: 'Menu',
      selected: false,
    },
    {
      title: 'Customizations',
      icon: 'tune',
      route: 'customization',
      group: 'Menu',
      selected: false,
    },

    {
      title: 'Restaurant Profile',
      icon: 'storefront',
      route: 'restaurant-profile',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Social Media',
      icon: 'campaign',
      route: 'social-media',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Zones',
      icon: 'pin_drop',
      route: 'zone',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Staff',
      icon: 'group',
      route: 'staffs',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Devices',
      icon: 'devices',
      route: 'device',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Stations',
      icon: 'print',
      route: 'station',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Hardware',
      icon: 'memory',
      route: 'hardware',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Delivery Partners',
      icon: 'local_shipping',
      route: 'delivery-partners',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'To Go',
      icon: 'shopping_bag',
      route: 'togo',
      group: 'Restaurant',
      selected: false,
    },
    {
      title: 'Settings',
      icon: 'settings',
      route: 'settings',
      group: 'Restaurant',
      selected: false,
    },

    {
      title: 'Payments',
      icon: 'credit_card',
      route: 'payment',
      group: 'Finance',
      selected: false,
    },
    { title: 'Tax', icon: 'percent', route: 'tax', group: 'Finance', selected: false },
    {
      title: 'Billing & Subscription',
      icon: 'account_balance_wallet',
      route: 'billing-and-subscription',
      group: 'Finance',
      selected: false,
    },

    {
      title: 'Reports',
      icon: 'bar_chart',
      route: 'report',
      group: 'Reports',
      selected: false,
    },

    {
      title: 'Profile & Account',
      icon: 'person',
      route: 'profile',
      group: 'Settings',
      selected: false,
    },
    {
      title: 'Appearance',
      icon: 'palette',
      route: 'appearance',
      group: 'Settings',
      selected: false,
    },
    {
      title: 'Security',
      icon: 'lock',
      route: 'change-password',
      group: 'Settings',
      selected: false,
    },
    {
      title: 'Notifications',
      icon: 'notifications',
      route: 'notifications',
      group: 'Settings',
      selected: false,
    },
  ];

  menuItems: any[] = [...this.baseMenuItems];

  private readonly menuGroupOrder = [
    '',
    'Operations',
    'Menu',
    'Restaurant',
    'Finance',
    'Reports',
    'Settings',
  ];

  get menuGroups(): { heading: string; items: any[] }[] {
    return this.menuGroupOrder
      .map((heading) => ({
        heading,
        items: this.menuItems.filter((item: any) => item.group === heading),
      }))
      .filter((group) => group.items.length > 0);
  }

  constructor() {
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        this.user = JSON.parse(user);
      }
    }
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
      this.configureSideMenus();
    });
    this.restaurantService.currentPlanName$.subscribe((planName: string | null) => {
      this.currentPlanName = planName;
      this.configureSideMenus();
    });
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      this.featureFlags = flags;
      this.configureSideMenus();
      this.loadReservationModeIfNeeded();
    });
    this.reservationService.mode$.subscribe((mode: ReservationMode | null) => {
      this.reservationMode = mode;
      this.configureSideMenus();
      // null again after a restaurant switch: fetch the new restaurant's mode.
      this.loadReservationModeIfNeeded();
    });
    this.reservationService.pendingRequestCount$.subscribe((count: number) => {
      this.pendingRequestCount = count;
    });
    this.restaurantService.restaurantDetailsRefresh$.subscribe(() => {
      this.getCurrentRestaurantDetails();
    });
    this.getCurrentRestaurantDetails();
    this.currentPath = this.router.url;
  }

  ngOnInit() {
    this.isSidebarExpanded = this.configService.isSidebarExpanded$.value;

    this.restaurantService.updateRestaurantLogo$.subscribe((restaurant: any) => {
      if (restaurant && restaurant.logo_url) {
        this.currentRestaurant.logo_url = restaurant.logo_url;
        this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
      }
      if (restaurant && restaurant.restaurant_name) {
        const tempName: string[] = restaurant.restaurant_name.trim().split(/\s+/);
        if (tempName.length > 1) {
          this.currentRestaurant.logo_avatar_name = tempName[0][0] + tempName[1][0];
        } else if (tempName.length === 1) {
          this.currentRestaurant.logo_avatar_name = tempName[0][0];
        } else {
          this.currentRestaurant.logo_avatar_name = '';
        }
      }
    });
    if (this.settingService.currentRestaurantSettings$.value == null) {
      this.getCurrentRestaurantSettings();
    }
  }

  getCurrentRestaurantSettings() {
    this.settingService.getSettings().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          return;
        }
        this.settingService.currentRestaurantSettings$.next(response.data);
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  getCurrentRestaurantDetails() {
    this.restaurantService.getRestaurantDetails().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          if (
            response.error.error?.message == 'Restaurant not found' ||
            response.error.error?.code == 404
          ) {
            this.router.navigate(['deactive-restaurant', 'not found']);
            return;
          }
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          this.authService.logout();
          this.router.navigate(['login']);
          return;
        }
        this.currentRestaurant = response.data;
        if (
          this.currentRestaurant &&
          this.currentRestaurant?.status !== 'activated' &&
          this.user?.user_role == 'restaurant_admin' &&
          !isLimitedAccessStatus(this.currentRestaurant?.status)
        ) {
          this.router.navigate(['deactive-restaurant', this.currentRestaurant?.status]);
          return;
        }
        this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
        this.restaurantService.restaurantPosProvider$.next(this.currentRestaurant.pos_provider);
        this.restaurantPosProvider = this.currentRestaurant.pos_provider;
        this.squareResyncService.checkAndPrompt(this.currentRestaurant);
        const tempName: string[] = this.currentRestaurant.restaurant_name.trim().split(/\s+/);
        if (tempName.length > 1) {
          this.currentRestaurant.logo_avatar_name = tempName[0][0] + tempName[1][0];
        } else if (tempName.length === 1) {
          this.currentRestaurant.logo_avatar_name = tempName[0][0];
        } else {
          this.currentRestaurant.logo_avatar_name = '';
        }
        this.configureSideMenus();
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  configureSideMenus() {
    // Always start from a fresh copy of the master list — this function's filters are
    // cumulative removals, never additive, so resetting here (rather than mutating
    // this.menuItems in place across calls) lets a later, less-restrictive re-evaluation
    // (e.g. a plan upgrade) actually restore items an earlier call removed.
    this.menuItems = [...this.baseMenuItems];

    if (isPlatformSupportUser()) {
      this.applyPlatformSupportMenus();
      this.syncRequestPolling();
      return;
    }
    if (isLimitedAccessStatus(this.currentRestaurant?.status)) {
      this.applyPublishedRestaurantMenus();
      // return;
    }

    const remove = (titles: string[]) => {
      this.menuItems = this.menuItems.filter((item: any) => !titles.includes(item.title));
    };
    // Social Media (FNF-1609) is for admins, managers and super admins only.
    if (!canManageSocialMedia(getLoggedInUserRole())) {
      remove(['Social Media']);
    }

    if (this.currentRestaurant?.status === 'activated') {
      // Locked, not removed — a visible-but-locked item with an "Upgrade to unlock"
      // prompt (onMenuItemClick) sells the upgrade; hiding it makes the feature look
      // like it doesn't exist. Direct navigation to the route is still blocked by
      // SubscriptionPlanGuard.
      const lockedForPlan = menuTitlesLockedForPlan(this.currentPlanName);
      this.menuItems = this.menuItems.map((item: any) => ({
        ...item,
        locked: lockedForPlan.includes(item.title),
        lockReason: 'plan',
      }));
    }

    // Until the feature flags load, nothing is removed for them.
    if (this.featureFlags) {
      const featureMenus: [string, string][] = [
        [FEATURE_FLAG_KEYS.HARDWARE, 'Hardware'],
        [FEATURE_FLAG_KEYS.TO_GO, 'To Go'],
        [FEATURE_FLAG_KEYS.DELIVERY_PARTNERS, 'Delivery Partners'],
        [FEATURE_FLAG_KEYS.RESERVATIONS, 'Reservations'],
        [FEATURE_FLAG_KEYS.TAX, 'Tax'],
        [FEATURE_FLAG_KEYS.BILLING, 'Billing & Subscription'],
        [FEATURE_FLAG_KEYS.SOCIAL_MEDIA, 'Social Media'],
      ];
      remove(
        featureMenus
          .filter(([key]) => !isFeatureActive(this.featureFlags, key))
          .map(([, title]) => title),
      );
    }
    // With reservations switched off for this restaurant the book is empty, so the
    // entry opens Reservation settings instead — the only place to switch them on.
    if (this.reservationMode === 'off') {
      this.menuItems = this.menuItems.map((item: any) =>
        item.title === 'Reservations' ? { ...item, route: 'reservation-settings' } : item,
      );
    }
    if (isPosManaged(this.restaurantPosProvider)) {
      remove(['Delivery Partners', 'Payments', 'Tax', 'Reports']);
    }
    // To Go and Payments are sections of Restaurant → Settings now, not pages.
    // Settings is listed while any of its sections applies: the order and
    // kitchen switches (RESTAURANT_SETTINGS), To Go (TO_GO), or Payments (no
    // Square / Clover / Swirepay). Until the flags load, nothing is removed for them.
    remove(['To Go', 'Payments']);
    if (this.featureFlags) {
      const hasSettingsSection =
        isFeatureActive(this.featureFlags, FEATURE_FLAG_KEYS.RESTAURANT_SETTINGS) ||
        isFeatureActive(this.featureFlags, FEATURE_FLAG_KEYS.TO_GO) ||
        !isPosManaged(this.restaurantPosProvider);
      if (!hasSettingsSection) remove(['Settings']);
    }
    this.syncRequestPolling();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.reservationService.stopRequestPolling();
  }

  /**
   * Polls for booking requests (FNF-308) while the Reservations entry is
   * there to carry the badge: rolled out, not locked, and a mode that can
   * produce requests. Anything else stops the poll and clears the badge.
   */
  private syncRequestPolling(): void {
    // configureSideMenus() still runs from subscriptions after destroy; a
    // sidebar that is gone must not restart the poll.
    if (this.destroyed) {
      return;
    }
    const entry = this.menuItems.find((item: any) => item.title === 'Reservations');
    const on =
      !!entry &&
      !entry.locked &&
      isFeatureActive(this.featureFlags, FEATURE_FLAG_KEYS.RESERVATIONS) &&
      !!this.reservationMode &&
      REQUEST_MODES.includes(this.reservationMode);
    if (on) {
      this.reservationService.startRequestPolling();
    } else {
      this.reservationService.stopRequestPolling();
    }
  }

  /** "99+" past two digits, like the header bell. */
  get pendingRequestBadge(): string {
    return this.pendingRequestCount > 99 ? '99+' : String(this.pendingRequestCount);
  }

  /** Reads the reservation mode once reservations are rolled out to this restaurant
   * and no mode is known yet; a failed read leaves the entry pointing at the book. */
  private loadReservationModeIfNeeded(): void {
    if (
      this.reservationMode ||
      !isFeatureActive(this.featureFlags, FEATURE_FLAG_KEYS.RESERVATIONS)
    ) {
      return;
    }
    this.reservationService.loadMode().subscribe({ error: () => {} });
  }

  /**
   * Platform support staff only troubleshoot menus and restaurant profile data,
   * so the sidebar is trimmed to those items.
   */
  applyPlatformSupportMenus() {
    this.menuItems = this.menuItems.filter((item: any) =>
      PLATFORM_SUPPORT_MENU_TITLES.includes(item.title),
    );
  }

  /**
   * A restaurant that's published but not yet fully activated can only manage
   * its public-facing profile/menu and account settings. Locked, not removed —
   * same reasoning as the plan-tier gate below: a visible, locked item shows
   * what's coming once the restaurant is activated, instead of looking like
   * the feature doesn't exist.
   */
  applyPublishedRestaurantMenus() {
    this.menuItems = this.menuItems.map((item: any) => ({
      ...item,
      locked: !isMenuTitleAllowedForStatus(item.title, this.currentRestaurant?.status),
      lockReason: 'status',
    }));
  }

  /** Locked items stay in the sidebar (see configureSideMenus()) but don't navigate —
   * clicking one explains why, based on what locked it (restaurant status vs plan tier). */
  onMenuItemClick(item: any, event: Event): void {
    if (!item.locked) {
      return;
    }
    event.preventDefault();
    const minPlan = minimumPlanForTitle(item.title);
    if (item.lockReason === 'status') {
      // A published-but-not-activated restaurant is gated by status now, but most of
      // these titles are ALSO plan-gated (see subscription-plan-menu.util.ts) — activation
      // alone won't unlock them unless the plan covers it too, so say so when it applies.
      this.toastr.info(
        minPlan
          ? `${item.title} unlocks once your restaurant is activated on the ${minPlan} plan or higher.`
          : `${item.title} unlocks once your restaurant is fully activated.`,
        'Not available yet',
      );
      return;
    }
    this.toastr.info(
      minPlan
        ? `${item.title} is included in the ${minPlan} plan. Upgrade from Billing & Subscription to unlock it.`
        : `${item.title} is included in a higher plan. Upgrade from Billing & Subscription to unlock it.`,
      'Upgrade required',
    );
  }

  /**
   * Subtitle under the restaurant name: the current subscription plan
   * ('Free' -> 'Free Plan'). Falls back to the signed-in user's role while the
   * plan is unresolved - super admin and platform support users never load a
   * subscription, so no plan name ever arrives for them.
   */
  get brandSubtitle(): string {
    const plan = (this.currentPlanName || '').trim();
    if (plan) {
      return /\bplan$/i.test(plan) ? plan : `${plan} Plan`;
    }
    return (this.user?.user_role || '')
      .split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onMenuThumbnailImageError(event: Event, itemId: string | number): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/default/logo.png';
    this.imageErrors.add(itemId);
  }

  hasImageError(itemId: string | number): boolean {
    return this.imageErrors.has(itemId);
  }

  /** Status dot on the logo's corner: yellow while only published — nothing to
   * flag once fully activated, so no dot shows then (or for any other
   * restaurant lifecycle status). */
  get restaurantStatusDotColor(): 'yellow' | null {
    const status = this.currentRestaurant?.status;
    if (status === 'published') {
      return 'yellow';
    }
    return null;
  }
}
